import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { LogIn } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const to = (location.state?.from?.pathname ? (location.state.from.pathname + (location.state.from.search || "")) : null) || (u.role === "candidate" ? "/candidate" : u.role === "owner" ? "/admin" : "/recruiter");
      navigate(to, { replace: true });
    } catch (err) {
      // Backend signals 2FA challenge with 401 + detail "2FA code required" + X-Require-2FA header
      const detail = err.response?.data?.detail || "";
      const headerFlag = err.response?.headers?.["x-require-2fa"];
      if (err.response?.status === 401 && (headerFlag === "1" || /2fa code required/i.test(detail))) {
        sessionStorage.setItem("pending_2fa", JSON.stringify({ email, password }));
        navigate("/2fa-verify", { replace: true });
        return;
      }
      setError(formatApiError(detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const { data: cfg } = await (await import("../lib/api")).api.get("/auth/google/config");
      if (!cfg.enabled) {
        setError("Google sign-in is not configured. Contact admin.");
        return;
      }
      const redirectUri = window.location.origin + "/auth/google";
      const state = "login";
      const params = new URLSearchParams({
        client_id: cfg.client_id,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        access_type: "online",
        include_granted_scopes: "true",
        prompt: "select_account",
        state,
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch (e) {
      setError("Could not start Google sign-in.");
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid grid-cols-12">
      <div className="col-span-12 lg:col-span-7 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="label-eyebrow">Ocean Ash Ltd — Admin Portal</div>
          <h1 className="font-display font-black text-5xl tracking-tight mt-3">Admin Portal.</h1>
          <p className="text-[#5C5C5C] dark:text-[#999] mt-3">Sign in to your account — owner, recruiter or candidate.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-[#0F0F0F] dark:border-[#444] bg-transparent px-4 py-3 rounded-sm text-base"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-[#0F0F0F] dark:border-[#444] bg-transparent px-4 py-3 rounded-sm text-base"
                placeholder="••••••••"
              />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-[var(--brand-primary)] font-medium">{error}</div>}
            <div className="flex items-center justify-between">
              <Link to="/forgot-password" data-testid="login-forgot" className="text-sm font-semibold underline underline-offset-4 hover:text-[var(--brand-primary)]">
                Forgot password?
              </Link>
            </div>
            <button data-testid="login-submit" disabled={loading} type="submit" className="btn-terra w-full text-base">
              {loading ? "Signing in…" : (<><LogIn className="w-4 h-4" /> Sign in</>)}
            </button>
          </form>

          <div className="my-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-[#E2E0D8] dark:bg-[#444]" />
            <span className="label-eyebrow">or</span>
            <div className="flex-1 h-px bg-[#E2E0D8] dark:bg-[#444]" />
          </div>

          <button
            data-testid="login-google"
            onClick={googleLogin}
            className="w-full border border-[#0F0F0F] dark:border-[#444] py-3 rounded-sm font-semibold hover:bg-[#0F0F0F] hover:text-[#FDFCF8] transition flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2L31 33.3c-2 1.5-4.4 2.4-7 2.4-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l6.4 5.5c-.5.5 6.6-4.8 6.6-15.1 0-1.3-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <p className="mt-10 text-sm text-[#5C5C5C] dark:text-[#999]">
            New here? <Link to="/register" data-testid="login-to-register" className="font-semibold text-[#0F0F0F] dark:text-[#FDFCF8] underline underline-offset-4 hover:text-[var(--brand-primary)]">Create an account</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block lg:col-span-5 bg-[#0F0F0F] relative">
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F0F0F]/95 via-[#0F0F0F]/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-[#FDFCF8]">
          <div className="label-eyebrow" style={{ color: "var(--brand-primary)" }}>Ocean Ash Ltd</div>
          <h2 className="font-display font-black text-4xl mt-3 leading-tight">
            Hire deliberately. <br />Apply without barriers.
          </h2>
          <p className="text-sm text-[#c2c2c2] mt-4">One place for jobs, candidates and pipeline.</p>
        </div>
      </div>
    </div>
  );
}
